import { InputNumber } from 'primeng/inputnumber';
import { FeatureKey } from 'src/app/_models';

/**
 * Whether deviations in a feature have the temperature control fields - the
 * optional temperature and the measured object. Mirrors the backend rule in
 * `DeviationWriteService`, which ignores both for every other feature.
 *
 * @param featureId the deviation's feature, e.g. `Feature_Mapping.FeatureId`
 */
export function isTemperatureControlFeature(
  featureId: number | null | undefined
): boolean {
  return (
    featureId === FeatureKey.FOOD_SAFETY ||
    featureId === FeatureKey.KUBA_CONTROL
  );
}

/**
 * Parses a temperature stored as text, such as `Deviation.TemperatureValue`.
 * Legacy rows were written with the server culture, so both "4,5" and "4.5"
 * occur.
 *
 * @param value the stored text
 * @returns the temperature, or `null` when the value is empty or not a number
 */
export function parseTemperatureValue(
  value: string | null | undefined
): number | null {
  if (value === null || value === undefined || value.trim() === '') {
    return null;
  }
  const parsed = parseFloat(value.replace(',', '.'));
  return isNaN(parsed) ? null : parsed;
}

/**
 * Lets a PrimeNG temperature field accept both "," and "." as the decimal
 * separator. PrimeNG only accepts the locale's own separator, so the other one
 * is inserted as the locale separator instead of the browser's keypress.
 *
 * Bind from a component method: `(onKeyDown)="onTemperatureKeyDown($event, input)"`.
 */
export function acceptBothDecimalSeparators(
  event: KeyboardEvent,
  input: InputNumber
): void {
  const isSeparator = event.key === ',' || event.key === '.';
  if (!isSeparator || event.key === input._decimalChar) {
    return;
  }
  event.preventDefault();
  input.insert(event, input._decimalChar, {
    isDecimalSign: true,
    isMinusSign: false
  });
}
